import clsx from 'clsx'
import React, {useContext, useState} from 'react'
import {Link, useHistory} from 'react-router-dom'
import {makeStyles} from 'external/material'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ShareIcon from '@mui/icons-material/Share'
import {faEdit, faPlusCircle, faMinusCircle, faFileExport, faSpinner} from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {TeacherContext} from 'teacher/context'
import PreviewQuestion from 'components/quiz/components/preview-question'

import {createPost} from 'helpers/command'
import {isEmpty} from 'helpers/utils'

import Settings from './settings'

const useStyles = makeStyles(questionStyles)

export default function Question(props) {

  const [expanded, setExpanded] = useState(false)
  const history = useHistory()
  const classes = useStyles()
  const context = useContext(TeacherContext)
  const {courseName, typeName, repository, owned, textpremise, shared} = props
  const {translate, link, linkQuery, query, quizQuestionBankCart: cart, updateState} = context

  const cartIndex = cart.findIndex(q => q.id == props.id)
  const inCart = cartIndex >= 0

  const expandClick = () => setExpanded(!expanded)

  const premiseLabel = isEmpty(textpremise) ? translate(typeName) : textpremise

  const [saving, setSaving] = React.useState(false)

  async function saveToPersonal () {

    setSaving(true)
    const [updatedId, cloned] = await createPost('quiz', 'saveBankQuestionToPersonal')({id: props.id})

    if (updatedId == props.id) {
      setSaving(false)
      history.push(linkQuery(link('quiz', 'question-bank'), {id: cloned.id}, true)) 
    }

  }


  function handleSaveToPersonal() {
    if (!saving) saveToPersonal()
  }
  function onCartClick() {
    const updateCommand = inCart
      ? cart.length == 1
              ? {quizQuestionBankCart: {$splice: [[cartIndex, 1]]}, quizQuestionBankCartEl: {$set: null}}
              : {quizQuestionBankCart: {$splice: [[cartIndex, 1]]}}
      : {quizQuestionBankCart: {$push: [props]}} 
    updateState(updateCommand)
  }

  React.useEffect(() => {
    if (isEmpty(textpremise)) setExpanded(true)
  }, [textpremise])

  return (
    <Box className={clsx(classes.root, {expanded})} >
      <Box className={classes.header}>
        <Box className={classes.cart}>
          <Box className={clsx(classes.cartButton, {inCart})} onClick={onCartClick}>
            <FontAwesomeIcon icon={inCart ? faMinusCircle : faPlusCircle} />
            <Box className={clsx(classes.cartLabel, {inCart})}>{
              translate(inCart ? 'remove-from-question-bank-cart' : 'add-to-question-bank-cart')
            }</Box>
          </Box>
        </Box>
        <Box className={classes.filler} />
        <Box className={classes.expand}>
          <Button className={classes.button} startIcon={expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />} onClick={expandClick} >{translate(expanded ? 'view-less' : 'view-more')}</Button>
        </Box>
        {!owned && (<>
          <Box className={classes.splitWrapper}>
            <Box className={classes.split} />
          </Box>
          <Box className={classes.saveToPersonal} onClick={handleSaveToPersonal}>
            {!saving && <><span><FontAwesomeIcon icon={faFileExport} /></span><span>{context.translate('save-to-personal')}</span></>}
            {saving && <span><FontAwesomeIcon icon={faSpinner} spin /></span>}
          </Box></>)
        }
        {owned && <>
          <Box className={classes.splitWrapper}>
            <Box className={classes.split} />
          </Box>
          <Box className={classes.edit}>
            <Tooltip title={translate('edit-question-tooltip')}>
              <Link to={link({sectionName: 'edit-question', questionId: props.id}, '/:languageId/:role/:page/:tab/:sectionName/:questionId', {query})} className={clsx(classes.link, classes.editLink)}>
                <FontAwesomeIcon icon={faEdit} />
              </Link>
            </Tooltip>
          </Box>
        <Box className={classes.splitWrapper}>
          <Box className={classes.split} />
        </Box>
        <Box className={classes.shareWrapper}>
          <Box className={classes.sharedLink}>
            <Box className={clsx(classes.sharedIcon, {shared})}><ShareIcon /></Box>
            <Box className={classes.sharedText}>{shared ? translate('shared') : '-'}</Box>
          </Box>
        </Box>
        <Box className={classes.settingsLink}>
          <Settings classes={classes} question={props} />
        </Box>
        </>}
      </Box>
      {!expanded && (<Box className={classes.textPremise}>{premiseLabel}</Box>)}
      {expanded && (<Box className={classes.content}>
        <PreviewQuestion headless showPoints showStandards context={context} question={props} />
      </Box>)}
      <Box className={classes.footer}>
        <Box className={classes.courseName}>{translate('course_assessment_header')}: {courseName}</Box>
        <Box className={classes.type}>{translate('type-header')}: {translate(typeName)}</Box>
        <Box className={classes.repository}>{translate('repository')}: {translate(owned ? 'personal' : repository)}</Box>
      </Box>
    </Box>
  )
}

function questionStyles() {

  const verticalCentering = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  }

  return {
    root: {
      marginTop: 10,
      '&:first-child': {
        marginTop:0
      },
      display: 'flex',
      flexDirection: 'column',
      border: 'solid 1px #becfd7',
      width: '100%',
      backgroundColor: '#fff',
    },
    saveToPersonal: {
      display: 'flex',
      flexDirection: 'row !important',
      justifyContent: 'flex-end',
      fontSize: '10pt',
      color: '#2ca5ec',
      cursor: 'pointer',
      '& span': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      },
      '& svg': {
        height: '18px',
        marginTop: -3,
        marginRight: 8
      }
    },
    cart: {
      paddingLeft: 8
    },
    cartButton: {
      display: 'flex',
      cursor: 'pointer',
      color: '#2a92eb',
      '&.inCart': {
        color: '#d33d60'
      },
      '& svg': {
        marginTop: 4
      }
    },
    cartLabel: {
      marginTop: 3,
      marginLeft: 5,
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    button: {
      color: '#2a92eb',
      fontSize: '9pt',
      textTransform: 'none',
      '& .MuiButton-startIcon': {
        marginRight: 5
      }
    },
    editLink: {
      color: '#2a92eb',
    },
    header: {
      padding: 10,
      display: 'flex',
      '& > div': {
        ...verticalCentering
      }
    },
    filler: {
      flexGrow: 1
    },
    split: {
      marginLeft: 15,
      marginRight: 15,
      backgroundColor: '#d4d3d9',
      width: 1,
      height: '45%'
    },
    sharedLink: {
      display: 'flex',
      marginRight: 20,
      color: '#4d586e'
    },
    sharedIcon: {
      ...verticalCentering,
      color:'#c4c4c4', 
      marginRight: 10,
      '& .MuiSvgIcon-root': {
        fontSize: '13.5pt'
      },
      '&.shared': {
        color: '#ea8511'
      }
    },
    sharedText: {
      ...verticalCentering,
      cursor: 'default',
      textAlign: 'center',
      marginRight: 10,
      width: 50,
      "&.selected": {
        color: "#fff"
      }
    },
    moreSettings: {
      ...verticalCentering
    },
    moreSettingsButton: {
      ...verticalCentering,
      color: '#a2a6b2',
      cursor: 'pointer',
      "&.selected": {
        color: '#fff'
      },
      '& .MuiSvgIcon-root': {
        fontSize: '16pt'
      }
    },
    content: {
      '& .section': {
        border: 0
      }
    },
    textPremise: {
      fontSize: '12pt',
      color: '#494f67',
      padding: '0 20px 20px 20px'
    },
    footer: {
      display: 'flex',
      backgroundColor: '#f7fbfe',
      color: '#494f67',
      fontSize: '10.5pt',
      borderTop: 'solid 1px #cfe0e8',
      padding: '10px',
      '& > div': {
        marginLeft: 15,
        borderLeft: 'solid 1px #cfe0e8',
        paddingLeft: 15,
        '&:first-child': {
          marginLeft: 10,
          borderLeft: 0,
          paddingLeft: 0
        }
      }
    }
  }
}


